// Поток оплаты звёздами в Mini App (E12-T12; ux/08 §3; tech/05 §7). Чистая логика, тестируется без DOM.
// Заказ создаётся с одним Idempotency-Key на окно покупки — двойной тап даёт один заказ.
// После openInvoice('paid') ждём начисления: WS payment.credited ИЛИ опрос GET /shop/orders/{id} раз в 1 с
// до 30 с ('pending' — раз в 3 с до 2 мин). «Готово» — только после статуса credited (P20).
import type { Api, Grant, ResponseOf, Wallet } from '@garages/contract';
import { ApiCallError, type ApiCall } from '../net/api-client.js';
import type { InvoiceStatus } from '../telegram/webapp.js';
import { t as tr } from '../i18n/t.js';

export type OrderStart =
  | { kind: 'opened'; orderId: string; status: InvoiceStatus }
  | { kind: 'error'; code: string; retryAfterS?: number };

export interface PayDeps {
  call: ApiCall;
  /** Окно счёта; `onLate` — статус, пришедший уже после того, как окно сочли закрытым (см. guardInvoice). */
  openInvoice: (url: string, onLate?: (status: InvoiceStatus) => void, onCancel?: (cb: () => void) => () => void, order?: { orderId: string; productId: string; poll: () => Promise<'paid' | 'pending' | null> }) => Promise<InvoiceStatus>;
  /** Подписка на payment.credited по WS; возвращает отписку. */
  onCredited: (cb: (orderId: string, grants: Grant, wallet: Wallet) => void) => () => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

export async function createAndOpen(d: PayDeps, productId: string, idempotencyKey: string, onLate?: (status: InvoiceStatus) => void, onCancel?: (cb: () => void) => () => void): Promise<OrderStart> {
  let order: ResponseOf<Api['createOrder']>;
  try {
    order = await d.call('createOrder', { body: { productId }, idempotencyKey });
  } catch (e) {
    if (e instanceof ApiCallError) return { kind: 'error', code: e.code, ...(e.retryAfterS ? { retryAfterS: e.retryAfterS } : {}) };
    throw e;
  }
  const orderId = order.orderId;
  const poll = async (): Promise<'paid' | 'pending' | null> => {
    const o = await d.call('getOrder', { params: { orderId }, noRetry: true });
    return o.status === 'paid' || o.status === 'credited' ? 'paid' : null;
  };
  const status = await d.openInvoice(order.invoiceLink, onLate, onCancel, { orderId, productId, poll });
  return { kind: 'opened', orderId: order.orderId, status };
}

/** Через сколько после возврата в игру без статуса окно счёта считаем закрытым (отмена). */
export const INVOICE_RETURN_MS = 2000;
/**
 * Через сколько без статуса окно счёта считаем закрытым в любом случае: в Telegram Web A окно счёта — оверлей
 * родительской страницы, и ни invoiceClosed, ни activated, ни фокус после крестика не приходят.
 */
export const INVOICE_TIMEOUT_MS = 20_000;

/** Окно счёта ответило failed быстрее — значит, не успело показаться (Telegram Web A, запуск кнопкой «Open»). */
export const INVOICE_FAST_FAIL_MS = 1500;
/** Опрос заказа, пока счёт открыт запасным путём (openTelegramLink не шлёт invoiceClosed). */
export const INVOICE_POLL_MS = 2000;

/** Как кончилось ожидание окна счёта — для аналитики и разбора сбоев. */
export interface InvoiceReport {
  status: InvoiceStatus;
  /** От открытия до итога, мс. */
  ms: number;
  /** Откуда итог: колбэк, событие invoiceClosed, «Отменить», возврат в игру, таймаут, опрос заказа. */
  via: 'callback' | 'event' | 'cancel' | 'return' | 'timeout' | 'poll';
  /** Окно не показалось (быстрый failed) и счёт открыт ссылкой t.me. */
  fallback: boolean;
  /** Первый ответ openInvoice при запасном пути: статус и время, мс. */
  first?: { status: InvoiceStatus; ms: number };
}

export interface InvoiceHost {
  /** WebApp.openInvoice(url, cb). */
  open: (url: string, cb: (status: InvoiceStatus) => void) => void;
  /** Событие Telegram invoiceClosed {url, status}; возвращает отписку. */
  onClosed: (cb: (url: string, status: InvoiceStatus) => void) => () => void;
  /** Игрок снова в игре: activated, visibilitychange → visible, focus, касание страницы; возвращает отписку. */
  onReturn: (cb: () => void) => () => void;
  /** Игрок нажал «Отменить» (или тап по затемнению) при открытом счёте; возвращает отписку. */
  onCancel?: (cb: () => void) => () => void;
  /** Запасной путь: открыть счёт ссылкой силами клиента Telegram (openTelegramLink); false — нечем. */
  openLink?: (url: string) => boolean;
  /** Статус заказа на сервере: paid/pending — оплачено; null — ещё нет. */
  poll?: () => Promise<'paid' | 'pending' | null>;
  report?: (r: InvoiceReport) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

/**
 * Окно счёта с защитой от «вечного спиннера». Статус — первый из: колбэк openInvoice, событие invoiceClosed по этому
 * адресу. Без них (Telegram Web A: окно счёта — оверлей родительской страницы, после крестика не приходит ничего):
 * «Отменить» или тап по затемнению, возврат в игру и INVOICE_RETURN_MS без статуса, INVOICE_TIMEOUT_MS — 'cancelled'
 * (заказ на сервере истечёт сам). failed быстрее INVOICE_FAST_FAIL_MS — окно не показалось: открываем счёт ссылкой
 * (openLink) и ждём дальше. Заказ опрашиваем всё время ожидания: оплату видно и без статуса Telegram. Настоящий статус, пришедший после отмены, уходит в `onLate`.
 */
export function guardInvoice(h: InvoiceHost, url: string, onLate?: (status: InvoiceStatus) => void): Promise<InvoiceStatus> {
  const setT = h.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearT = h.clearTimer ?? ((t: unknown) => clearTimeout(t as ReturnType<typeof setTimeout>));
  const now = h.now ?? Date.now;
  const t0 = now();
  return new Promise<InvoiceStatus>((resolve) => {
    let done = false;
    let synthetic = false; // окно сочли закрытым сами, без статуса Telegram
    let lateSent = false;
    let fallback = false;
    let first: InvoiceReport['first'];
    let back: unknown = null;
    let hard: unknown = null;
    let poller: unknown = null;
    let pollUntil = 0;
    const offs: { ret?: () => void; closed?: () => void; cancel?: () => void } = {};
    const drop = (t: unknown) => { if (t !== null) clearT(t); };
    const startPoll = () => {
      if (!h.poll || poller !== null) return;
      const tick = () => {
        poller = null;
        if (lateSent || (done && !synthetic) || now() > pollUntil) return;
        void h.poll!().then((st) => { if (st) finish(st, true, 'poll'); else poller = setT(tick, INVOICE_POLL_MS); }, () => { poller = setT(tick, INVOICE_POLL_MS); });
      };
      poller = setT(tick, INVOICE_POLL_MS);
    };
    function finish(st: InvoiceStatus, real: boolean, via: InvoiceReport['via']): void {
      if (!done && real && st === 'failed' && !fallback && now() - t0 < INVOICE_FAST_FAIL_MS && h.openLink) {
        // Окно не показалось — открыть счёт ссылкой; итог дальше — «Отменить», таймаут или опрос заказа.
        first = { status: st, ms: now() - t0 };
        if (h.openLink(url)) {
          fallback = true;
          drop(hard); hard = setT(() => { hard = null; finish('cancelled', false, 'timeout'); }, INVOICE_TIMEOUT_MS);
          pollUntil = now() + 10 * 60_000;
          startPoll();
          return;
        }
      }
      drop(back); back = null;
      drop(hard); hard = null;
      offs.ret?.(); delete offs.ret;
      offs.cancel?.(); delete offs.cancel;
      if (real) { offs.closed?.(); delete offs.closed; drop(poller); poller = null; }
      if (!done) {
        done = true; synthetic = !real; resolve(st);
        h.report?.({ status: st, ms: now() - t0, via, fallback, ...(first ? { first } : {}) });
        if (synthetic) {
          // Ждём позднего статуса не дольше жизни заказа (10 мин): событие и опрос заказа.
          pollUntil = now() + 10 * 60_000;
          startPoll();
          setT(() => { offs.closed?.(); delete offs.closed; }, 10 * 60_000);
        }
        return;
      }
      // Окно сочли закрытым, а статус пришёл позже — отдаём его один раз (оплатил — ждём начисления).
      if (real && synthetic && !lateSent && st !== 'cancelled' && st !== 'failed') { lateSent = true; drop(poller); poller = null; onLate?.(st); }
    }
    offs.ret = h.onReturn(() => {
      if (done || back !== null) return;
      back = setT(() => { back = null; finish('cancelled', false, 'return'); }, INVOICE_RETURN_MS);
    });
    offs.closed = h.onClosed((u, st) => { if (u === url) finish(st, true, 'event'); });
    const offCancel = h.onCancel?.(() => finish('cancelled', false, 'cancel'));
    if (offCancel) offs.cancel = offCancel;
    hard = setT(() => { hard = null; finish('cancelled', false, 'timeout'); }, INVOICE_TIMEOUT_MS);
    // Заказ опрашиваем сразу: запасные пути (ссылка t.me, window.open) статуса не присылают.
    pollUntil = now() + 10 * 60_000;
    startPoll();
    try { h.open(url, (st) => finish(st, true, 'callback')); } catch { finish('failed', true, 'callback'); }
  });
}

export type WaitResult = { kind: 'credited'; grants: Grant; wallet: Wallet } | { kind: 'timeout' } | { kind: 'failed'; status: string };

export const POLL = { paid: { everyMs: 1000, forMs: 30_000 }, pending: { everyMs: 3000, forMs: 120_000 } } as const;

export async function waitCredited(d: PayDeps, orderId: string, mode: 'paid' | 'pending', signal?: { cancelled: boolean }): Promise<WaitResult> {
  const sleep = d.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = d.now ?? Date.now;
  let viaWs: WaitResult | null = null;
  const off = d.onCredited((id, grants, wallet) => { if (id === orderId) viaWs = { kind: 'credited', grants, wallet }; });
  const until = now() + POLL[mode].forMs;
  try {
    while (now() < until && !signal?.cancelled) {
      if (viaWs) return viaWs;
      try {
        const o = await d.call('getOrder', { params: { orderId }, noRetry: true });
        if (o.status === 'credited' && o.credited) return { kind: 'credited', grants: o.credited.grants, wallet: o.credited.wallet };
        if (o.status === 'failed' || o.status === 'cancelled' || o.status === 'expired' || o.status === 'refunded') return { kind: 'failed', status: o.status };
      } catch { /* сеть — продолжаем ждать: сервер начислит сам */ }
      if (viaWs) return viaWs;
      await sleep(POLL[mode].everyMs);
    }
    return viaWs ?? { kind: 'timeout' };
  } finally {
    off();
  }
}

/** Названия пакетов — канон §10 (числа и ID приходят из shop.catalog, Р-07). */
export const PACK_NAMES: Record<string, string> = {
  'PACK-STARTER': tr('CL-PAY-FLOW-01'), 'PACK-HANDFUL': tr('CL-PAY-FLOW-02'), 'PACK-PACK': tr('CL-PAY-FLOW-03'), 'PACK-BOX': tr('CL-PAY-FLOW-04'), 'PACK-SACK': tr('CL-PAY-FLOW-05'), 'PACK-WAGON': tr('CL-PAY-FLOW-06'),
};

/** «Стартовый» — один раз за жизнь аккаунта (badge сервера `starter`; в фикстурах — `once`). */
export function isOnce(p: { productId: string; badge: string | null }): boolean {
  return p.badge === 'starter' || p.badge === 'once' || p.productId === 'PACK-STARTER';
}

/** Метка плитки по ux/08 §1.2: бонус к базовому курсу самого дешёвого пакета («+6,7%»), у стартового — «×3 · один раз». */
export function packLabel(p: { productId: string; priceXtr: number; grant: Grant; badge: string | null }, baseRate: number): string | null {
  const talons = p.grant.currencies?.talons ?? 0;
  const k = talons / (p.priceXtr * baseRate);
  if (isOnce(p)) return tr('CL-PAY-FLOW-07', { p0: Math.round(k) });
  const pct = Math.round((k - 1) * 1000) / 10;
  return pct > 0.05 ? `+${String(pct).replace('.', ',')}%` : null;
}

export function baseRate(products: { productId: string; priceXtr: number; grant: Grant; badge: string | null }[]): number {
  const regular = products.filter((p) => !isOnce(p) && p.grant.currencies?.talons);
  if (!regular.length) return 2;
  const cheapest = regular.reduce((a, b) => (a.priceXtr <= b.priceXtr ? a : b));
  return (cheapest.grant.currencies!.talons!) / cheapest.priceXtr;
}
